import React from "react";

import { getApiUrl } from "@/config";
import InteractionService, { InteractionApi } from "@/service/Interaction";
import LocalStorageService from "@/service/LocalStorage";
import {
  ApiResponse,
  QuizVote,
  QuizVoteResponse,
  Slide,
} from "@/service/Model";
import TrackingService from "@/service/Tracking";

export type QuizProviderProps = {
  quizId: string;
  slides: Slide[];
  preview: boolean;
};

export type QuizProviderState = {
  votes: QuizVote[];
  isPreview: boolean;
  correctAnswersCount: number;
  hasAllAnswersCorrect: boolean;
  onVote: (vote: QuizVote) => Promise<any>;
  hasVotes: () => boolean;
  resetVotes: () => Promise<any>;
  getCorrectAnswers: () => number;
  isSlideVoted: (slideId: string) => boolean;
  isQuizVoted: () => boolean;
};

const QuizContext = React.createContext<QuizProviderState | null>(null);

export class QuizProvider extends React.Component<
  QuizProviderProps,
  QuizProviderState
> {
  private api: InteractionApi;

  public constructor(props: QuizProviderProps) {
    super(props);

    let votes: QuizVote[] = [];

    this.onVote = this.onVote.bind(this);
    this.hasVotes = this.hasVotes.bind(this);
    this.resetVotes = this.resetVotes.bind(this);
    this.getCorrectAnswers = this.getCorrectAnswers.bind(this);
    this.isSlideVoted = this.isSlideVoted.bind(this);
    this.isQuizVoted = this.isQuizVoted.bind(this);

    this.api = new InteractionService(getApiUrl(props.preview));

    if (!props.preview && props.quizId) {
      votes =
        (LocalStorageService.getVotes(props.quizId) as QuizVote[]) || votes;

      if (votes.length) {
        // Asynchronously update correct answers count from server
        this.fetchCorrectAnswers(votes).then((correctAnswersCount: number) =>
          this.setState({
            correctAnswersCount: correctAnswersCount,
            hasAllAnswersCorrect: correctAnswersCount === votes.length,
          })
        );
      }
    }

    this.state = {
      votes: votes,
      onVote: this.onVote,
      hasVotes: this.hasVotes,
      resetVotes: this.resetVotes,
      isPreview: props.preview,
      isSlideVoted: this.isSlideVoted,
      isQuizVoted: this.isQuizVoted,
      getCorrectAnswers: this.getCorrectAnswers,
      correctAnswersCount: 0,
      hasAllAnswersCorrect: false,
    };
  }

  public render(): JSX.Element {
    return (
      <QuizContext.Provider value={{ ...this.state }}>
        {this.props.children}
      </QuizContext.Provider>
    );
  }

  private onVote(vote: QuizVote): Promise<void> {
    const { isPreview, votes, correctAnswersCount } = this.state;

    return this.api
      .getQuizCorrectAnswersCount(this.props.quizId, [vote])
      .then((response: ApiResponse<QuizVoteResponse>) => {
        if (!response.data) {
          return;
        }
        const { correctAnswerCount, correctAnswers } = response.data;

        vote.answer = correctAnswerCount === 1;
        const newCount = correctAnswersCount + correctAnswerCount;

        if (correctAnswers) {
          vote.correctAnswers = correctAnswers;
        }
        const mergedVotes = [...votes, vote];

        if (!isPreview) {
          this.sendVote(vote);
          LocalStorageService.setVotes(vote.quizId, mergedVotes);
          TrackingService.getInstance().trackOnVote();
        }

        this.setState({
          votes: mergedVotes,
          correctAnswersCount: newCount,
          hasAllAnswersCorrect: mergedVotes.length === newCount,
        });
      });
  }

  private hasVotes(): boolean {
    return this.state.votes && this.state.votes.length > 0;
  }

  private resetVotes(): Promise<any> {
    return new Promise((resolve: Function) => {
      const { isPreview } = this.state;
      const { quizId } = this.props;

      if (!isPreview) {
        LocalStorageService.setVotes(quizId, undefined);
      }

      this.setState(
        {
          votes: [],
          correctAnswersCount: 0,
          hasAllAnswersCorrect: false,
        },
        resolve()
      );
    });
  }

  private fetchCorrectAnswers(votes: Array<any>): Promise<any> {
    return this.api
      .getQuizCorrectAnswersCount(this.props.quizId, votes)
      .then((response: ApiResponse<QuizVoteResponse>) => {
        return response.data!.correctAnswerCount;
      })
      .catch((error: Error) => {
        throw error;
      });
  }

  private isSlideVoted(slideId: string): boolean {
    if (!this.state.votes) {
      return false;
    }
    return !!this.state.votes.find(
      (knownVote: QuizVote) => knownVote.slideId === slideId
    );
  }

  private isQuizVoted(): boolean {
    const { slides } = this.props;
    const { votes } = this.state;

    if (slides && slides.length) {
      const slidesKeys = slides.reduce((acc: any, curr: any): any => {
        acc[curr.id] = !!(votes && votes.length
          ? votes.find((vote: QuizVote) => vote.slideId === curr.id)
          : false);
        return acc;
      }, {});
      const votedSlides = Object.keys(slidesKeys).filter(
        (key: string) => slidesKeys[key]
      );

      return votedSlides.length === slides.length;
    }

    return false;
  }

  private getCorrectAnswers(): number {
    return this.state.correctAnswersCount;
  }

  private sendVote(vote: QuizVote): void {
    this.api.postQuizResults(
      vote.quizId,
      vote.slideId,
      vote.optionId,
      !this.hasVotes()
    );
  }
}

export const QuizConsumer = QuizContext.Consumer;

export default QuizContext;
